import React, { useEffect, useState, useCallback, useRef } from "react"
import buildInfo from "../../buildInfo.json"

/**
 * VersionChecker
 * ─────────────────────────────────────────────────────────────────
 * Detecta cuando se publicó un deploy nuevo del portal y muestra un
 * banner no-bloqueante al usuario para que recargue cuando quiera.
 *
 * Cómo funciona:
 *   1. Al iniciar, el bundle "sabe" su propia versión vía buildInfo.json
 *      (generado por scripts/generate-version.js en el prebuild).
 *   2. Cada `POLL_INTERVAL_MS` consulta GET /version.json?t=<timestamp>
 *      (el query string evita caché del CDN/navegador).
 *   3. Si la `version` remota difiere de la local, muestra el banner.
 *   4. El usuario decide: "Actualizar" (reload) o "Después" (lo oculta
 *      hasta el próximo poll; el banner reaparecerá si sigue desactualizado).
 *
 * En modo "dev" (version === "dev") no hace nada — el dev server
 * recarga solo con HMR.
 */

const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5 minutos
const POLL_AFTER_DISMISS_MS = 10 * 60 * 1000 // si lo cerró, vuelve a chequear en 10 min

const VersionChecker = () => {
  const localVersion = buildInfo?.version || "dev"
  const isDev = localVersion === "dev"

  const [remoteInfo, setRemoteInfo] = useState(null)
  const [dismissed, setDismissed] = useState(false)
  const dismissedAtRef = useRef(0)
  const isReloadingRef = useRef(false)

  const fetchVersion = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      })
      if (!res.ok) return
      const data = await res.json()
      if (data && data.version) setRemoteInfo(data)
    } catch {
      // sin red o servidor caído — ignorar; el próximo poll volverá a intentar
    }
  }, [])

  useEffect(() => {
    if (isDev) return undefined
    // Check inicial corto (3s después de cargar)
    const initialT = setTimeout(fetchVersion, 3000)
    const intervalT = setInterval(fetchVersion, POLL_INTERVAL_MS)
    // Re-chequea cuando la pestaña vuelve a estar visible (usuario regresa
    // a la pestaña después de horas → seguro que hay nueva versión)
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchVersion()
    }
    document.addEventListener("visibilitychange", onVisibility)
    return () => {
      clearTimeout(initialT)
      clearInterval(intervalT)
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [isDev, fetchVersion])

  // Si el usuario hizo dismiss, restablecemos el banner cuando pasó
  // suficiente tiempo y sigue habiendo versión nueva.
  useEffect(() => {
    if (!dismissed) return undefined
    const t = setInterval(() => {
      if (Date.now() - dismissedAtRef.current >= POLL_AFTER_DISMISS_MS) {
        setDismissed(false)
      }
    }, 30 * 1000)
    return () => clearInterval(t)
  }, [dismissed])

  if (isDev) return null
  if (!remoteInfo) return null
  if (remoteInfo.version === localVersion) return null
  if (dismissed) return null

  const handleReload = () => {
    if (isReloadingRef.current) return
    isReloadingRef.current = true
    // Hard reload — fuerza ir al servidor (no al caché del navegador)
    window.location.reload()
  }

  const handleDismiss = () => {
    dismissedAtRef.current = Date.now()
    setDismissed(true)
  }

  return (
    <div role="status" aria-live="polite" style={{
      position: "fixed",
      top: 12,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 2000,
      background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
      color: "#fff",
      borderRadius: 12,
      boxShadow: "0 10px 30px rgba(15, 23, 42, 0.25)",
      padding: "12px 18px",
      display: "flex",
      alignItems: "center",
      gap: 14,
      flexWrap: "wrap",
      maxWidth: "min(640px, calc(100vw - 24px))",
      animation: "vc-slide-in .35s cubic-bezier(.2,.8,.2,1)",
    }}>
      <style>{`
        @keyframes vc-slide-in {
          from { transform: translate(-50%, -100%); opacity: 0; }
          to   { transform: translate(-50%, 0); opacity: 1; }
        }
      `}</style>
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: "rgba(255,255,255,0.18)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <i className="mdi mdi-rocket-launch-outline" style={{ fontSize: 20 }} />
      </div>
      <div style={{ flex: 1, minWidth: 200, lineHeight: 1.35 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          Hay una nueva versión disponible
        </div>
        <div style={{ fontSize: 12, opacity: 0.9 }}>
          Actualiza para evitar errores y obtener las mejoras más recientes.
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <button
          type="button"
          onClick={handleReload}
          style={{
            background: "#fff", color: "#1e40af",
            border: "none", borderRadius: 8,
            padding: "7px 14px", fontSize: 13, fontWeight: 700,
            cursor: "pointer",
          }}
        >
          <i className="mdi mdi-refresh me-1" />
          Actualizar
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          style={{
            background: "transparent", color: "#fff",
            border: "1px solid rgba(255,255,255,0.4)", borderRadius: 8,
            padding: "7px 12px", fontSize: 13, fontWeight: 600,
            cursor: "pointer",
          }}
          title="Recordar más tarde"
        >
          Después
        </button>
      </div>
    </div>
  )
}

export default VersionChecker
